/**
 * Match a pile of screenshots to rows by file name: "orzedits0.png",
 * "@orzedits0 reply.jpg" and "Orz_Edits0 (2).webp" all belong to
 * @orzedits0. Case, @ and punctuation are ignored. When a name contains
 * several handles the longest wins, so "@ed" doesn't steal "@fred.png".
 */

export const SCREENSHOT_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const SCREENSHOT_ACCEPT = 'image/jpeg,image/png,image/webp';
export const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024;

/** Why a file can't be used as a screenshot, or null when it can. */
export function screenshotProblem(file: { type: string; size: number }): string | null {
  if (!SCREENSHOT_TYPES.includes(file.type)) return 'Not a JPEG, PNG or WebP image';
  if (file.size > MAX_SCREENSHOT_BYTES) return 'Over 5 MB';
  return null;
}

/** Letters and digits only, lower case. */
export function squash(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function baseName(fileName: string): string {
  const leaf = fileName.split(/[\\/]/).pop() ?? fileName;
  return leaf.replace(/\.[a-z0-9]{2,5}$/i, '');
}

export interface MatchTarget {
  key: number;
  username: string;
}

export interface MatchOutcome {
  /** File index → row key. */
  matched: Map<number, number>;
  /** File indexes that matched no row (or tied between rows). */
  unmatched: number[];
  /** File indexes that matched a row another file already took. */
  extra: number[];
}

/**
 * Pair files with rows. A file goes to the longest handle its name
 * contains; a tie between two different handles of the same length leaves
 * the file unmatched rather than guessing. Each row takes the first file
 * that matches it; later ones are reported as extras.
 */
export function matchScreenshots(fileNames: ReadonlyArray<string>, targets: ReadonlyArray<MatchTarget>): MatchOutcome {
  const byHandle = new Map<string, number>();
  for (const t of targets) {
    const h = squash(t.username);
    if (h.length >= 2 && !byHandle.has(h)) byHandle.set(h, t.key);
  }
  // Longest first so the first hit is the most specific.
  const handles = [...byHandle.keys()].sort((a, b) => b.length - a.length);

  const matched = new Map<number, number>();
  const taken = new Set<number>();
  const unmatched: number[] = [];
  const extra: number[] = [];

  fileNames.forEach((name, i) => {
    const s = squash(baseName(name));
    let hit: string | null = null;
    let tie = false;
    for (const h of handles) {
      if (hit && h.length < hit.length) break;
      if (s.includes(h)) {
        if (hit) tie = true;
        else hit = h;
      }
    }
    if (!hit || tie) {
      unmatched.push(i);
      return;
    }
    const key = byHandle.get(hit)!;
    if (taken.has(key)) {
      extra.push(i);
      return;
    }
    taken.add(key);
    matched.set(i, key);
  });
  return { matched, unmatched, extra };
}
